import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  DocumentTextIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  ChartBarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const statisticsCards = [
  {
    title: 'Toplam Vakalar',
    key: 'total',
    icon: DocumentTextIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  {
    title: 'Bekleyen Vakalar',
    key: 'pending',
    icon: ClockIcon,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
  },
  {
    title: 'Devam Eden',
    key: 'in_progress',
    icon: ExclamationTriangleIcon,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  {
    title: 'Tamamlanan',
    key: 'completed',
    icon: CheckCircleIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
];

export default function DashboardPage() {
  const { user } = useAuthStore();

  // Fetch dashboard statistics
  // 112 başkanlık kullanıcısı için yılbaşından itibaren filtrele
  const isExternalPresidency = user?.role === 'external_presidency';
  const isExternalHospital = user?.role === 'external_hospital';
  const yearStart = `${new Date().getFullYear()}-01-01`;

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', isExternalPresidency ? yearStart : null, user?.hospital_id],
    queryFn: () => {
      const params = isExternalPresidency ? `?date_from=${yearStart}` : '';
      return api.get(`/cases/stats${params}`).then(res => res.data);
    },
  });

  // Fetch recent cases
  const { data: recentCases, isLoading: casesLoading } = useQuery({
    queryKey: ['recent-cases', isExternalPresidency ? yearStart : null, user?.hospital_id],
    queryFn: () => {
      const params = isExternalPresidency
        ? `?limit=5&sort=createdAt:desc&date_from=${yearStart}`
        : '?limit=5&sort=createdAt:desc';
      return api.get(`/cases${params}`).then(res => res.data);
    },
  });

  // Fetch today's assignments for current user
  const { data: myAssignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['my-assignments'],
    queryFn: () => api.get('/cases/my-assignments').then(res => res.data),
    enabled: ['paramedic', 'team_leader'].includes(user?.role),
  });

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Günaydın';
    if (hour < 18) return 'İyi günler';
    return 'İyi akşamlar';
  };

  // Helper to safely read stats depending on backend shape
  const getCardValue = (card) => {
    const data = stats?.data || stats;
    if (!data) return 0;

    switch (card.key) {
      case 'total':
        return data.total || 0;
      case 'pending':
        // Bekleyen = henüz başlanmış / initiated durumundaki vakalar
        return data.by_status?.initiated || 0;
      case 'in_progress':
        return data.by_status?.in_progress || 0;
      case 'completed':
        return data.by_status?.completed || 0;
      default:
        return 0;
    }
  };

  // Build series for status distribution chart
  const statusSeries = [
    { key: 'initiated', label: 'Başlatıldı', color: 'bg-blue-500' },
    { key: 'in_progress', label: 'Devam Eden', color: 'bg-yellow-500' },
    { key: 'completed', label: 'Tamamlanan', color: 'bg-green-500' },
    { key: 'cancelled', label: 'İptal Edilen', color: 'bg-red-500' },
  ].map(item => {
    const data = stats?.data || stats;
    const value = data?.by_status?.[item.key] ?? 0;
    return { ...item, value };
  });

  const maxStatusValue = Math.max(
    ...statusSeries.map(s => (typeof s.value === 'number' ? s.value : 0)),
    1
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      'initiated': { label: 'Başlatıldı', color: 'bg-yellow-100 text-yellow-800' },
      'in_progress': { label: 'Devam Ediyor', color: 'bg-orange-100 text-orange-800' },
      'completed': { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
      'cancelled': { label: 'İptal Edildi', color: 'bg-red-100 text-red-800' },
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'low': { label: 'Düşük', color: 'bg-blue-100 text-blue-800' },
      'medium': { label: 'Orta', color: 'bg-yellow-100 text-yellow-800' },
      'high': { label: 'Yüksek', color: 'bg-orange-100 text-orange-800' },
      'critical': { label: 'Kritik', color: 'bg-red-100 text-red-800' },
    };

    const config = priorityConfig[priority] || { label: priority, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {getGreeting()},{' '}
            {(user?.first_name || user?.firstName || '')}{' '}
            {(user?.last_name || user?.lastName || '')}!
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {(user?.first_name || user?.firstName || '')}{' '}
            {(user?.last_name || user?.lastName || '')}
            {user?.department?.name ? ` • ${user.department.name}` : ''}
          </p>
        </div>
        {!isExternalPresidency && (
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link to="/cases/new">
            <Button variant="medical" className="inline-flex items-center">
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Vaka
            </Button>
          </Link>
        </div>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statisticsCards.map((card) => {
          const Icon = card.icon;
          const value = getCardValue(card);
          
          return (
            <Card key={card.key}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-500 truncate">
                      {card.title}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statsLoading ? (
                        <div className="animate-pulse h-8 bg-gray-200 rounded w-16"></div>
                      ) : (
                        value
                      )}
                    </p>
                  </div>
                  <div className={`flex-shrink-0 ${card.bgColor} rounded-lg p-3 flex items-center justify-center`}>
                    <Icon className={`h-7 w-7 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Cases */}
        <Card>
          <CardHeader>
            <CardTitle>Son Vakalar</CardTitle>
            <CardDescription>
              En son oluşturulan vakalar
            </CardDescription>
          </CardHeader>
          <CardContent>
            {casesLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex space-x-4">
                    <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                    <div className="flex-1 space-y-2 py-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (recentCases?.data?.cases || recentCases?.cases || []).length === 0 ? (
              <p className="text-gray-500 text-center py-4">Henüz vaka bulunmuyor</p>
            ) : (
              <div className="space-y-4">
                {(recentCases?.data?.cases || recentCases?.cases || []).slice(0, 5).map((case_) => (
                  <div key={case_.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-medical-100 flex items-center justify-center">
                        <DocumentTextIcon className="h-6 w-6 text-medical-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {case_.patient_name} • {case_.case_number}
                      </p>
                      <p className="text-sm text-gray-500">
                        {case_.patient_address || case_.destination_address || '-'}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        {getStatusBadge(case_.status)}
                        {case_.priority && getPriorityBadge(case_.priority)}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Link
                        to={`/cases/${case_.id}`}
                        className="text-medical-600 hover:text-medical-900 text-sm font-medium"
                      >
                        Görüntüle
                      </Link>
                    </div>
                  </div>
                ))}
                
                <div className="pt-2">
                  <Link
                    to="/cases"
                    className="text-sm font-medium text-medical-600 hover:text-medical-900"
                  >
                    Tüm vakaları görüntüle →
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Assignments (for paramedics and team leaders) */}
        {['paramedic', 'team_leader'].includes(user?.role) && (
          <Card>
            <CardHeader>
              <CardTitle>Bugünkü Görevlerim</CardTitle>
              <CardDescription>
                Size atanmış aktif vakalar
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignmentsLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse flex space-x-4">
                      <div className="rounded-full bg-gray-200 h-10 w-10"></div>
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : myAssignments?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Bugün size atanmış görev bulunmuyor</p>
              ) : (
                <div className="space-y-4">
                  {myAssignments?.slice(0, 5).map((assignment) => (
                    <div key={assignment.id} className="flex items-center space-x-4 p-3 bg-orange-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <UsersIcon className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {assignment.patientName} • {assignment.caseNumber}
                        </p>
                        <p className="text-sm text-gray-500">
                          {assignment.transportType}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          {getStatusBadge(assignment.status)}
                          <span className="text-xs text-gray-500">
                            {new Date(assignment.requestTime).toLocaleTimeString('tr-TR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Link
                          to={`/cases/${assignment.id}`}
                          className="text-orange-600 hover:text-orange-900 text-sm font-medium"
                        >
                          Görüntüle
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions (for non-paramedic roles) */}
        {!['paramedic'].includes(user?.role) && (
          <Card>
            <CardHeader>
              <CardTitle>Hızlı İşlemler</CardTitle>
              <CardDescription>
                Sık kullanılan işlemler
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {!isExternalPresidency && (
                <Link
                  to="/cases/new"
                  className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <PlusIcon className="h-8 w-8 text-medical-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Yeni Vaka Oluştur</p>
                    <p className="text-sm text-gray-500">Acil durum kaydı başlat</p>
                  </div>
                </Link>
                )}
                
                <Link
                  to="/cases?filter=pending"
                  className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ClockIcon className="h-8 w-8 text-yellow-600 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">Bekleyen Vakalar</p>
                    <p className="text-sm text-gray-500">Atama bekleyen vakalar</p>
                  </div>
                </Link>

                {['admin', 'supervisor'].includes(user?.role) && (
                  <Link
                    to="/users"
                    className="flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <UsersIcon className="h-8 w-8 text-blue-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Kullanıcı Yönetimi</p>
                      <p className="text-sm text-gray-500">Personel ve roller</p>
                    </div>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Status distribution chart (for admin/supervisor) */}
      {['admin', 'supervisor'].includes(user?.role) && (
        <Card>
          <CardHeader>
            <CardTitle>Vaka Durumu Dağılımı</CardTitle>
            <CardDescription>
              Duruma göre toplam vaka sayıları
            </CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                    <div className="h-2 bg-gray-200 rounded w-full"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {statusSeries.map((item) => (
                  <div key={item.key}>
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="font-medium text-gray-700">{item.label}</span>
                      <span className="text-gray-600">{item.value}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`${item.color} h-2 rounded-full transition-all`}
                        style={{ width: `${(item.value / maxStatusValue) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
} 